import React, {createContext, useContext, useState} from 'react';

const LanguageContext = createContext();

const translations = {
    de: {
        nav: {
            home: 'Home',
            externalScan: 'Externer Scan',
            internalScan: 'Interner Scan',
            pricing: 'Preise',
            awareness: 'Awareness',
            contact: 'Kontakt',
            login: 'Login'
        },
        hero: {
            title: 'Cyber Security Scanner',
            subtitle: 'Cyberangriffe frühzeitig erkennen, Risiken aktiv minimieren',
            description: 'Fehlkonfigurationen, vergessene Geräte, fehlende Updates und geleakte Passwörter öffnen Angreifern Tür und Tor. Scanfire prüft regelmässig, was über Ihr Unternehmen von aussen sichtbar ist, was in Ihrem internen Netz verwundbar ist und ob Konten Ihrer Mitarbeitenden in Datenlecks auftauchen. Die Ergebnisse sehen Sie im Browser, als PDF-Bericht und auf Wunsch als Benachrichtigung per E-Mail.',
            cta: 'Jetzt starten'
        },
        threatMap: {
            title: 'Live Cyber-Bedrohungen Weltweit',
            subtitle: 'Echtzeit-Monitoring globaler Cyberangriffe'
        },
        dimensions: {
            title: 'Die 4 Bereiche von Scanfire',
            subtitle: 'Scanfire kombiniert vier Sicherheits-Perspektiven in einer einzigen Lösung, mit einem gemeinsamen Risikowert von 0 bis 100',
            external: {
                title: 'Extern',
                points: [
                    'Sicht aus der Perspektive eines Angreifers auf Ihre Domains und öffentlichen IP-Adressen',
                    'Offene Ports und Dienste, bekannte Schwachstellen (CVE), Webserver und Zertifikate',
                    'E-Mail-Sicherheit (SPF, DMARC, DKIM) und Nachahmer-Domains'
                ]
            },
            internal: {
                title: 'Intern',
                points: [
                    'Schwachstellen in Ihrem internen Netz: Server, Clients, NAS, Drucker und vergessene Geräte',
                    'Eine Scan-Appliance in Ihrem Netz oder ein dedizierter Host via VPN liefert die Ergebnisse an Scanfire',
                    'Befunde je Host und Port, mit CVE und Schweregrad'
                ]
            },
            darknet: {
                title: 'Darknet',
                points: [
                    'E-Mail-Adressen Ihrer Domains, die in Datenlecks auftauchen',
                    'Erkennung, ob zusätzlich ein Passwort bekannt ist',
                    'Benachrichtigung, sobald neue Einträge auftauchen'
                ]
            },
            cloud: {
                title: 'Cloud',
                points: [
                    'Sicherheitseinstellungen Ihres Microsoft-365-Tenants',
                    'Einmalige Anmeldung durch einen globalen Administrator, sein Passwort erreicht Scanfire nie',
                    'Verfügbar als Zusatzmodul Microsoft 365'
                ]
            }
        },
        benefits: {
            transparency: {
                title: 'Mehr Transparenz, weniger Risiko',
                description: 'Nach jedem Scan vergleicht Scanfire das Ergebnis mit dem vorherigen. Sie erfahren, was neu ist und was verschwunden ist:',
                points: [
                    'E-Mail-Benachrichtigung pro Bereich, ab dem Schweregrad Ihrer Wahl',
                    'Optionaler Monatsbericht: der vollständige Bericht als PDF im Anhang',
                    'Weitere Empfänger wie ein Security-Postfach, ein Ticketsystem oder ein externer Security Officer',
                    'Die E-Mail nennt nur Anzahl und Schweregrad, Details sehen Sie erst nach der Anmeldung'
                ]
            },
            affordable: {
                title: 'Bezahlbare Sicherheit, auch für KMU',
                description: 'Im Vergleich zu gleichwertigen Lösungen, die oft mit hohem personellem Aufwand und Kosten verbunden sind, ist Scanfire speziell für kleine Unternehmen und KMU konzipiert. Die Lösung liefert professionelle Berichte und tiefgehende Analysen zu einem Preis, der auch für Firmen mit begrenzten Ressourcen tragbar ist.'
            },
            certification: {
                title: 'Basis für Zertifizierungen & Kundenmanagement',
                description: 'Jeder Scan bleibt mit Verlauf und PDF-Bericht nachvollziehbar, derselbe Scan ergibt immer denselben Bericht. Akzeptierte Risiken sind mit Person, Datum und Ablauf dokumentiert. Damit liefert Scanfire eine wertvolle Grundlage für:',
                points: [
                    'IT-Audits und Zertifizierungen',
                    'Kunden- und Compliance-Anforderungen',
                    'Transparente Kommunikation mit Management und Stakeholdern',
                    'IT-Dienstleister, die ihren Kunden Status und Risiken aufzeigen'
                ]
            }
        },
        contact: {
            title: 'Kontakt',
            description: 'Kontaktieren Sie uns – wir unterstützen Sie beim Setup und stehen Ihnen mit langjähriger Erfahrung im Bereich Cyber Security beratend zur Seite. Zusätzlich begleiten wir Sie mit Awareness-Schulungen, realistischen Phishing-Simulationen sowie professionellen Gap-Analysen.',
            form: {
                name: 'Name',
                email: 'E-Mail Adresse',
                subject: 'Betreff',
                message: 'Ihre Nachricht',
                submit: 'Nachricht senden',
                sending: 'Wird gesendet...',
                success: 'Nachricht erfolgreich gesendet!',
                error: 'Fehler beim Senden. Bitte versuchen Sie es erneut.'
            }
        },
        externalScan: {
            title: 'Funktionen: Externer Scan & Darknet',
            subtitle: 'Was von aussen über Ihre Systeme sichtbar ist, geprüft von neun spezialisierten Scannern. Ohne Installation, ohne Agent.',
            features: [
                'Kein Setup nötig: Domains und öffentliche IP-Adressen erfassen, der nächste Scan prüft sie.',
                'Risikowert von 0 bis 100: Zustand jeder Organisation auf einen Blick, von «Alles gut» bis «Gefährdet».',
                'Ansicht pro Host: Befunde je Schweregrad, offene Ports und erkannte Dienste mit Version.',
                'CVE-Details: CVE-Nummer, CVSS-Wert, betroffener Port und Produkt, Erklärung und Verweis.',
                'Web, Zertifikate & E-Mail: Webserver, Zertifikate sowie SPF, DMARC und DKIM.',
                'Nachahmer-Domains: fremde Domains, die Ihren ähneln, mit Markierung neu registrierter.',
                'Gefundene Hosts: neue Hosts unter Ihren Domains über DNS und Zertifikate, mit einem Klick übernehmen.',
                'Darknet-Monitoring: Konten Ihrer Domains in Datenlecks, inklusive Hinweis auf bekannte Passwörter.',
                'Befunde akzeptieren: bewusst in Kauf genommene Risiken dokumentieren, auf Wunsch mit Ablaufdatum.',
                'Suche & Verlauf: alle Befunde durchsuchen und die Entwicklung über frei wählbare Zeiträume verfolgen.'
            ],
            scannersTitle: '9 Scanner für Extern & Darknet',
            scannersSubtitle: 'Jeder Scanner läuft nach seinem eigenen Zeitplan. Scanfire zeigt, welcher Scanner wann zuletzt gelaufen ist und mit welchem Ergebnis.',
            scanners: [
                'Exponierte Metadaten',
                'Externe Infrastruktur',
                'Digitale Aussenfläche',
                'Bekannte Web-Schwachstellen',
                'Mail-Sicherheit',
                'Offene Ports und Dienste',
                'Web-Sicherheit',
                'Nicht erfasste eigene Hosts',
                'Darknet-Prüfung'
            ],
            screenshots: {
                overview: {
                    title: 'Übersicht & Risikowert',
                    description: 'Alle Organisationen auf einen Blick: Zustand, Risikowert, schwerster CVSS-Wert, offene Ports und Zeitpunkt des letzten Ergebnisses.'
                },
                hosts: {
                    title: 'Ansicht pro Host',
                    description: 'Jede Domain und jede Adresse als eigene Zeile, mit Befunden je Schweregrad und offenen Ports. Der schwerste Fall steht oben.'
                },
                findingDetail: {
                    title: 'Befunde im Detail',
                    description: 'CVE-Nummer, CVSS-Wert, Port, Produkt und Version, Erklärung und Verweis. Jeder Befund lässt sich direkt akzeptieren.'
                },
                scanners: {
                    title: 'Scanner & Fortschritt',
                    description: 'Während ein Scan läuft, zeigt Scanfire den Fortschritt je Scanner. Erste Ergebnisse sind bereits sichtbar.'
                },
                lookalikeDomains: {
                    title: 'Nachahmer-Domains',
                    description: 'Domains, die jemand anderes registriert hat und die Ihren ähneln. Prüfen Sie diese auf Anmeldeseiten und lassen Sie sie bei Bedarf sperren.'
                },
                discoveredHosts: {
                    title: 'Gefundene Hosts',
                    description: 'Scanfire findet weitere Hosts unter Ihren Domains über DNS, Zertifikate und Leak-Daten. Übernehmen oder verwerfen, fertig.'
                },
                darknet: {
                    title: 'Darknet-Monitoring',
                    description: 'Konten Ihrer Domains, die in Datenlecks vorkommen, mit Quelle, Zeitpunkt und konkreter Massnahme.'
                }
            }
        },
        internalScan: {
            title: 'Funktionen: Interner Scan, Berichte & Alarme',
            subtitle: 'Eine Scan-Appliance in Ihrem Netz oder ein dedizierter Host via VPN-Verbindung prüft interne Systeme auf Schwachstellen. Alle Bereiche laufen im Scanfire-Portal zusammen, mit Verlauf, PDF-Berichten und Benachrichtigungen.',
            features: [
                'Scan-Appliance im eigenen Netz oder dedizierter Host via VPN-Verbindung: Einrichtung durch Ihren Partner oder Scanfire.',
                'Interne Schwachstellen: zum Beispiel RDP, SMB oder Log4j, mit Host, Port und CVE.',
                'Regelmässige Scans nach Plan: zusätzlich jederzeit «Jetzt scannen», mit Fortschritt je Scanner.',
                'Verlauf: offene Ports, Schwachstellen und Schweregrade über frei wählbare Zeiträume.',
                'PDF-Berichte je Bereich: in Deutsch oder Englisch, mit Management Summary für die Geschäftsleitung.',
                'Benachrichtigungen: neue und verschwundene Befunde nach jedem Scan, per E-Mail ab wählbarem Schweregrad.',
                'Rollen: Benutzer mit Leserechten oder Administrator, Einladung direkt aus dem Portal.',
                'Partner-Zugriff: betreut Sie ein IT-Partner, sieht er Ihre Ergebnisse ebenfalls. Portal in Deutsch und Englisch.'
            ],
            screenshots: {
                tabs: {
                    title: 'Ein Reiter pro Bereich',
                    description: 'Extern, Intern und Darknet, dazu «Akzeptiert» und «Berichte». Die Zahl zeigt die offenen Befunde je Bereich.'
                },
                internalTrend: {
                    title: 'Verlauf der internen Befunde',
                    description: 'Wie sich kritische, hohe, mittlere und niedrige Befunde im internen Netz über die Scans entwickeln.'
                },
                internalFinding: {
                    title: 'Interne Befunde je Host & Port',
                    description: 'Was die Scan-Appliance oder der VPN-Host gemeldet hat, aufklappbar mit Ziel, Host, Port, CVE und Zeitpunkt.'
                },
                reports: {
                    title: 'Berichte je Scan',
                    description: 'Für jeden Scan ein PDF-Bericht je Bereich, in Deutsch oder Englisch. Derselbe Scan ergibt immer denselben Bericht.'
                },
                reportSummary: {
                    title: 'Management Summary',
                    description: 'Jeder Bericht beginnt mit einer Zusammenfassung für die Geschäftsleitung und zeigt die Entwicklung über die vorherigen Scans.'
                },
                notifications: {
                    title: 'E-Mail-Benachrichtigungen',
                    description: 'Pro Bereich festlegen, ab welchem Schweregrad eine E-Mail kommt, weitere Adressen ergänzen und den Monatsbericht aktivieren.'
                }
            }
        },
        pricing: {
            title: 'Pricing',
            subtitle: 'Die Anmeldung bei Scanfire ist kostenlos. Jede Registrierung startet im Plan Free: eine Domain, einmalig von aussen geprüft. Einen anderen Plan können Sie bei der Registrierung gleich mit anfragen.',
            vatNote: 'exkl. 8.1% MwSt.',
            ctaRegister: 'Registrieren',
            ctaLearnMore: 'Mehr erfahren',
            plans: [
                {
                    name: 'Free',
                    price: 'CHF 0',
                    period: 'einmalig',
                    features: [
                        '1 Organisation',
                        '1 Benutzer',
                        '1 Domain',
                        '1 Scan, nur Extern',
                        'CVE-Details ab einem bezahlten Plan',
                        'ohne Report',
                        'Keine Mindestlaufzeit'
                    ]
                },
                {
                    name: 'Darknet Pro',
                    price: 'CHF 59',
                    period: 'monatlich',
                    features: [
                        'Bereiche: Extern und Darknet',
                        'Maximal 5 Domains',
                        'PDF-Report inklusive',
                        'Darknet Suche nach Datenbeständen Ihrer Domain',
                        'Darknet Suche von Breaches, Kennwörtern und Spoofing',
                        'Keine Installation notwendig, keine Setup Kosten',
                        'Mindestlaufzeit 6 Monate'
                    ]
                },
                {
                    name: 'Fullscan Essential',
                    price: 'CHF 99',
                    period: 'monatlich',
                    features: [
                        'Bereiche: Intern, Extern und Darknet',
                        'Bis zu 5 VLANs',
                        'max 50 Benutzer pro Organisation',
                        'regelmässige Scans und Reports von extern und internem Security Monitoring',
                        'Web Portal View mit allen Details',
                        'Mail Alerts',
                        'Darknet Collection Abfrage',
                        'VPN und 3 Stunden Support inklusive',
                        'Setup Kosten einmalig CHF 190.--',
                        'Mindestlaufzeit 6 Monate'
                    ],
                    popular: true
                },
                {
                    name: 'Fullscan Pro',
                    price: 'CHF 299',
                    period: 'monatlich',
                    features: [
                        'Bereiche: Intern, Extern und Darknet',
                        'Ab 5 VLANs',
                        'Unlimitierte Benutzer',
                        'regelmässige Scans und Reports von extern und internem Security Monitoring',
                        'Web Portal View mit allen Details',
                        'Mail Alerts',
                        'Darknet Collection Abfrage',
                        'VPN und 3 Stunden Support inklusive',
                        'Setup Kosten einmalig CHF 190.--',
                        'Mindestlaufzeit 6 Monate'
                    ]
                }
            ],
            addonsTitle: 'Zusatzmodule',
            addons: [
                {
                    name: 'Microsoft 365 Hardening',
                    price: 'CHF 39',
                    period: 'monatlich pro Tenant',
                    note: 'Anbindung über eine einmalige Microsoft-Anmeldung, Ihr Passwort erreicht Scanfire nie',
                    features: [
                        'Microsoft Entra ID Analyse',
                        'Exchange Online Hardening',
                        'Teams Sicherheitsanalyse',
                        'SharePoint Sicherheitsanalyse',
                        'OneDrive Analyse',
                        'Conditional Access Prüfung',
                        'MFA Analyse',
                        'Secure Score Bewertung',
                        'Empfehlungen inkl. Priorisierung',
                        'Laufende Überwachung neuer Schwachstellen'
                    ]
                },
                {
                    name: 'Assessment Deep External',
                    price: 'CHF 500',
                    period: 'einmalig',
                    note: 'Bis zu 5 Domainnamen',
                    features: [
                        'Tiefgehende OSINT-Analyse',
                        'Darknet-Recherche',
                        'Social Media Analyse',
                        'Leaked Credentials',
                        'Domain Exposure',
                        'DNS-, Mail- und Infrastrukturprüfung',
                        'Personensuche (öffentlich verfügbare Quellen)',
                        'Identifikation möglicher Angriffsvektoren',
                        'Executive Summary & Management Report',
                        'Handlungsempfehlungen'
                    ]
                }
            ],
            annualDiscount: {
                title: 'Jahresabo sparen',
                value: '-15%',
                note: 'bei jährlicher Zahlung'
            }
        },
        awareness: {
            title: 'Awareness Training',
            subtitle: 'Schulungen und Sensibilisierung für Ihre Mitarbeiter',
            description: 'Zusätzlich begleiten wir Sie mit Awareness-Schulungen, realistischen Phishing-Simulationen sowie professionellen Gap-Analysen.'
        },
        footer: {
            tagline: 'Scanfire.ch – Ihre Cyberabwehr mit Weitblick. Effizient. Neutral. Bezahlbar.',
            privacy: 'Datenschutzerklärung',
            impressum: 'Impressum'
        },
        impressum: {
            title: 'Impressum',
            company: 'Magal GmbH',
            address: 'Haselstrasse 33, 5400 Baden',
            email: 'info@magal.ch',
            commercialRegister: {
                title: 'Handelsregistereintrag',
                name: 'Magal GmbH',
                number: 'CHE-206.226.851',
                office: 'Handelsregisteramt: AG'
            },
            representatives: {
                title: 'Vertretungsberechtigte Personen',
                names: 'Maurizio Galati'
            },
            vatNumber: {
                title: 'Mehrwertsteuernummer',
                number: 'CHE-206.226.851'
            },
            webdesign: {
                title: 'Webdesign',
                name: 'appox.ch GmbH',
                address: 'Haselstrasse 33, 5400 Baden',
                email: 'hello@appox.ch'
            },
            disclaimer: {
                title: 'Haftungsausschluss',
                content: 'Der Autor übernimmt keinerlei Gewähr hinsichtlich der inhaltlichen Richtigkeit, Genauigkeit, Aktualität, Zuverlässigkeit und Vollständigkeit der Informationen. Haftungsansprüche gegen den Autor wegen Schäden materieller oder imaterieller Art, welche aus dem Zugriff oder der Nutzung bzw. Nichtnutzung der veröffentlichten Informationen, durch Missbrauch der Verbindung oder durch technische Störungen entstanden sind, werden ausgeschlossen. Alle Angebote sind unverbindlich. Der Autor behält es sich ausdrücklich vor, Teile der Seiten oder das gesamte Angebot ohne gesonderte Ankündigung zu verändern, zu ergänzen, zu löschen oder die Veröffentlichung zeitweise oder endgültig einzustellen.'
            },
            links: {
                title: 'Haftung für Links',
                content: 'Verweise und Links auf Webseiten Dritter liegen ausserhalb urseres Verantwortungsbereichs. Es wird jegliche Verantwortung für solche Webseiten abgelehnt. Der Zugriff und die Nutzung solcher Webseiten erfolgen auf eigene Gefahr des Nutzers oder der Nutzerin.'
            },
            copyright: {
                title: 'Urheberrechte',
                content: 'Die Urheber- und alle anderen Rechte an Inhalten, Bildern, Fotos oder anderen Dateien auf der Website gehören ausschliesslich der Firma Magal GmbH oder den speziell genannten Rechtsinhabern. Für die Reproduktion jeglicher Elemente ist die schriftliche Zustimmung der Urheberrechtsträger im Voraus einzuholen.'
            }
        }
    },
    en: {
        nav: {
            home: 'Home',
            externalScan: 'External Scan',
            internalScan: 'Internal Scan',
            pricing: 'Pricing',
            awareness: 'Awareness',
            contact: 'Contact',
            login: 'Login'
        },
        hero: {
            title: 'Cyber Security Scanner',
            subtitle: 'Detect cyber attacks early, actively minimize risks',
            description: 'Misconfigurations, forgotten devices, missing updates and leaked passwords open doors for attackers. Scanfire regularly checks what is visible about your company from the outside, what is vulnerable in your internal network and whether your employees\' accounts appear in data leaks. You see the results in your browser, as a PDF report and, if you wish, as an email notification.',
            cta: 'Get Started'
        },
        threatMap: {
            title: 'Live Cyber Threats Worldwide',
            subtitle: 'Real-time monitoring of global cyber attacks'
        },
        dimensions: {
            title: 'The 4 Areas of Scanfire',
            subtitle: 'Scanfire combines four security perspectives in a single solution, with one shared risk score from 0 to 100',
            external: {
                title: 'External',
                points: [
                    'An attacker\'s view of your domains and public IP addresses',
                    'Open ports and services, known vulnerabilities (CVE), web servers and certificates',
                    'Email security (SPF, DMARC, DKIM) and lookalike domains'
                ]
            },
            internal: {
                title: 'Internal',
                points: [
                    'Vulnerabilities in your internal network: servers, clients, NAS, printers and forgotten devices',
                    'A scan appliance in your network or a dedicated host via VPN delivers the results to Scanfire',
                    'Findings per host and port, with CVE and severity'
                ]
            },
            darknet: {
                title: 'Darknet',
                points: [
                    'Email addresses of your domains that appear in data leaks',
                    'Detection of whether a password is known as well',
                    'Notification as soon as new entries appear'
                ]
            },
            cloud: {
                title: 'Cloud',
                points: [
                    'Security settings of your Microsoft 365 tenant',
                    'One-time sign-in by a global administrator, their password never reaches Scanfire',
                    'Available as the Microsoft 365 add-on module'
                ]
            }
        },
        benefits: {
            transparency: {
                title: 'More Transparency, Less Risk',
                description: 'After every scan, Scanfire compares the result with the previous one. You learn what is new and what has disappeared:',
                points: [
                    'Email notification per area, from the severity of your choice',
                    'Optional monthly report: the complete report as a PDF attachment',
                    'Additional recipients such as a security mailbox, a ticket system or an external Security Officer',
                    'The email only states count and severity, details are visible after signing in'
                ]
            },
            affordable: {
                title: 'Affordable Security, Even for SMEs',
                description: 'Compared to equivalent solutions, which often involve high personnel costs and expenses, Scanfire is specifically designed for small businesses and SMEs. The solution delivers professional reports and in-depth analyses at a price that is affordable even for companies with limited resources.'
            },
            certification: {
                title: 'Basis for Certifications & Customer Management',
                description: 'Every scan stays traceable with its history and PDF report, the same scan always produces the same report. Accepted risks are documented with person, date and expiry. This makes Scanfire a valuable foundation for:',
                points: [
                    'IT audits and certifications',
                    'Customer and compliance requirements',
                    'Transparent communication with management and stakeholders',
                    'IT service providers showing their customers status and risks'
                ]
            }
        },
        contact: {
            title: 'Contact',
            description: 'Contact us – we support you with setup and provide advisory services based on our extensive experience in cyber security. Additionally, we accompany you with awareness training, realistic phishing simulations, and professional gap analyses.',
            form: {
                name: 'Name',
                email: 'Email Address',
                subject: 'Subject',
                message: 'Your Message',
                submit: 'Send Message',
                sending: 'Sending...',
                success: 'Message sent successfully!',
                error: 'Error sending message. Please try again.'
            }
        },
        externalScan: {
            title: 'Features: External Scan & Darknet',
            subtitle: 'What is visible about your systems from the outside, checked by nine specialised scanners. No installation, no agent.',
            features: [
                'No setup required: add domains and public IP addresses, the next scan checks them.',
                'Risk score from 0 to 100: the state of every organization at a glance, from "All good" to "At risk".',
                'Per host view: findings per severity, open ports and detected services with version.',
                'CVE details: CVE number, CVSS score, affected port and product, explanation and reference.',
                'Web, certificates & email: web servers, certificates as well as SPF, DMARC and DKIM.',
                'Lookalike domains: third-party domains resembling yours, newly registered ones are flagged.',
                'Discovered hosts: new hosts under your domains via DNS and certificates, adopt them with one click.',
                'Darknet monitoring: accounts of your domains in data leaks, including a hint on known passwords.',
                'Accept findings: document consciously accepted risks, optionally with an expiry date.',
                'Search & history: search all findings and follow the trend over any time range.'
            ],
            scannersTitle: '9 scanners for External & Darknet',
            scannersSubtitle: 'Each scanner runs on its own schedule. Scanfire shows which scanner last ran when, and with which result.',
            scanners: [
                'Exposed metadata',
                'External infrastructure',
                'Internet footprint',
                'Known web vulnerabilities',
                'Mail security',
                'Open ports and services',
                'Web security',
                'Your unregistered hosts',
                'Darknet check'
            ],
            screenshots: {
                overview: {
                    title: 'Overview & Risk Score',
                    description: 'All organizations at a glance: state, risk score, highest CVSS score, open ports and time of the latest result.'
                },
                hosts: {
                    title: 'Per Host View',
                    description: 'Every domain and every address as its own row, with findings per severity and open ports. The most severe case comes first.'
                },
                findingDetail: {
                    title: 'Findings in Detail',
                    description: 'CVE number, CVSS score, port, product and version, explanation and reference. Every finding can be accepted directly.'
                },
                scanners: {
                    title: 'Scanners & Progress',
                    description: 'While a scan is running, Scanfire shows the progress per scanner. First results are already visible.'
                },
                lookalikeDomains: {
                    title: 'Lookalike Domains',
                    description: 'Domains registered by someone else that resemble yours. Check them for login pages and have them taken down if needed.'
                },
                discoveredHosts: {
                    title: 'Discovered Hosts',
                    description: 'Scanfire finds further hosts under your domains via DNS, certificates and leak data. Adopt or discard, done.'
                },
                darknet: {
                    title: 'Darknet Monitoring',
                    description: 'Accounts of your domains that appear in data leaks, with source, date and a concrete remedy.'
                }
            }
        },
        internalScan: {
            title: 'Features: Internal Scan, Reports & Alerts',
            subtitle: 'A scan appliance in your network or a dedicated host via VPN connection checks internal systems for vulnerabilities. All areas come together in the Scanfire portal, with history, PDF reports and notifications.',
            features: [
                'Scan appliance in your own network or a dedicated host via VPN connection: set up by your partner or Scanfire.',
                'Internal vulnerabilities: for example RDP, SMB or Log4j, with host, port and CVE.',
                'Regular scheduled scans: plus "Scan now" at any time, with progress per scanner.',
                'History: open ports, vulnerabilities and severities over any time range.',
                'PDF reports per area: in German or English, with a management summary for executives.',
                'Notifications: new and resolved findings after every scan, by email from a chosen severity.',
                'Roles: read-only users or administrators, invitations directly from the portal.',
                'Partner access: if an IT partner supports you, they see your results too. Portal in German and English.'
            ],
            screenshots: {
                tabs: {
                    title: 'One Tab per Area',
                    description: 'External, Internal and Darknet, plus "Accepted" and "Reports". The number shows the open findings per area.'
                },
                internalTrend: {
                    title: 'Internal Findings Over Time',
                    description: 'How critical, high, medium and low findings in the internal network develop across scans.'
                },
                internalFinding: {
                    title: 'Internal Findings per Host & Port',
                    description: 'What the scan appliance or VPN host reported, expandable with target, host, port, CVE and time.'
                },
                reports: {
                    title: 'Reports per Scan',
                    description: 'A PDF report per area for every scan, in German or English. The same scan always produces the same report.'
                },
                reportSummary: {
                    title: 'Management Summary',
                    description: 'Every report starts with a summary for executives and shows the development over the previous scans.'
                },
                notifications: {
                    title: 'Email Notifications',
                    description: 'Set per area from which severity an email is sent, add further addresses and enable the monthly report.'
                }
            }
        },
        pricing: {
            title: 'Pricing',
            subtitle: 'Registration at Scanfire is free. Every registration starts on the Free plan: one domain, checked once from the outside. You can request a different plan right during registration.',
            vatNote: 'excl. 8.1% VAT',
            ctaRegister: 'Register',
            ctaLearnMore: 'Learn more',
            plans: [
                {
                    name: 'Free',
                    price: 'CHF 0',
                    period: 'one-time',
                    features: [
                        '1 Organization',
                        '1 User',
                        '1 Domain',
                        '1 Scan, external only',
                        'CVE details from a paid plan',
                        'without Report',
                        'No minimum term'
                    ]
                },
                {
                    name: 'Darknet Pro',
                    price: 'CHF 59',
                    period: 'monthly',
                    features: [
                        'Areas: External and Darknet',
                        'Maximum 5 Domains',
                        'PDF Report included',
                        'Darknet search for data of your domain',
                        'Darknet search for breaches, passwords and spoofing',
                        'No installation necessary, no setup costs',
                        'Minimum term 6 months'
                    ]
                },
                {
                    name: 'Fullscan Essential',
                    price: 'CHF 99',
                    period: 'monthly',
                    features: [
                        'Areas: Internal, External and Darknet',
                        'Up to 5 VLANs',
                        'max 50 Users per Organization',
                        'regular scans and reports for external and internal security monitoring',
                        'Web Portal View with all details',
                        'Mail Alerts',
                        'Darknet Collection Query',
                        'VPN and 3 hours support included',
                        'Setup costs one-time CHF 190.--',
                        'Minimum term 6 months'
                    ],
                    popular: true
                },
                {
                    name: 'Fullscan Pro',
                    price: 'CHF 299',
                    period: 'monthly',
                    features: [
                        'Areas: Internal, External and Darknet',
                        'From 5 VLANs',
                        'Unlimited Users',
                        'regular scans and reports for external and internal security monitoring',
                        'Web Portal View with all details',
                        'Mail Alerts',
                        'Darknet Collection Query',
                        'VPN and 3 hours support included',
                        'Setup costs one-time CHF 190.--',
                        'Minimum term 6 months'
                    ]
                }
            ],
            addonsTitle: 'Add-on Modules',
            addons: [
                {
                    name: 'Microsoft 365 Hardening',
                    price: 'CHF 39',
                    period: 'monthly per tenant',
                    note: 'Connected via a one-time Microsoft sign-in, your password never reaches Scanfire',
                    features: [
                        'Microsoft Entra ID Analysis',
                        'Exchange Online Hardening',
                        'Teams Security Analysis',
                        'SharePoint Security Analysis',
                        'OneDrive Analysis',
                        'Conditional Access Review',
                        'MFA Analysis',
                        'Secure Score Assessment',
                        'Recommendations incl. Prioritization',
                        'Continuous monitoring of new vulnerabilities'
                    ]
                },
                {
                    name: 'Assessment Deep External',
                    price: 'CHF 500',
                    period: 'one-time',
                    note: 'Up to 5 domain names',
                    features: [
                        'In-depth OSINT analysis',
                        'Darknet research',
                        'Social Media Analysis',
                        'Leaked Credentials',
                        'Domain Exposure',
                        'DNS, Mail and Infrastructure Review',
                        'People Search (publicly available sources)',
                        'Identification of possible attack vectors',
                        'Executive Summary & Management Report',
                        'Recommended Actions'
                    ]
                }
            ],
            annualDiscount: {
                title: 'Save with annual plan',
                value: '-15%',
                note: 'when paying annually'
            }
        },
        awareness: {
            title: 'Awareness Training',
            subtitle: 'Training and awareness for your employees',
            description: 'Additionally, we accompany you with awareness training, realistic phishing simulations and professional gap analyses.'
        },
        footer: {
            tagline: 'Scanfire.ch – Your cyber defense with foresight. Efficient. Neutral. Affordable.',
            privacy: 'Privacy Policy',
            impressum: 'Imprint'
        },
        impressum: {
            title: 'Imprint',
            company: 'Magal GmbH',
            address: 'Haselstrasse 33, 5400 Baden',
            email: 'info@magal.ch',
            commercialRegister: {
                title: 'Commercial Register Entry',
                name: 'Magal GmbH',
                number: 'CHE-206.226.851',
                office: 'Commercial Register Office: AG'
            },
            representatives: {
                title: 'Authorized Representatives',
                names: 'Maurizio Galati'
            },
            vatNumber: {
                title: 'VAT Number',
                number: 'CHE-206.226.851'
            },
            webdesign: {
                title: 'Web Design',
                name: 'appox.ch GmbH',
                address: 'Haselstrasse 33, 5400 Baden',
                email: 'hello@appox.ch'
            },
            disclaimer: {
                title: 'Disclaimer',
                content: 'The author assumes no liability for the correctness, accuracy, timeliness, reliability and completeness of the information. Liability claims against the author for material or immaterial damage resulting from access to or use or non-use of the published information, through misuse of the connection or due to technical faults are excluded. All offers are non-binding. The author expressly reserves the right to change, supplement or delete parts of the pages or the entire range without prior notice or to temporarily or permanently cease publication.'
            },
            links: {
                title: 'Liability for Links',
                content: 'References and links to third-party websites are outside our area of responsibility. Any responsibility for such websites is rejected. Access to and use of such websites is at the user\'s own risk.'
            },
            copyright: {
                title: 'Copyright',
                content: 'The copyright and all other rights to content, images, photos or other files on the website belong exclusively to Magal GmbH or the specifically named rights holders. For the reproduction of any elements, the written consent of the copyright holder must be obtained in advance.'
            }
        }
    }
};

export const LanguageProvider = ({children}) => {
    const [language, setLanguage] = useState('de');

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'de' ? 'en' : 'de');
    };

    const t = translations[language];

    return (
        <LanguageContext.Provider value={{language, toggleLanguage, t}}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
